import { api } from "./baseApi";
import { inventoryApi } from "./inventoryApi";
import { ApiResponse, Transaction, PaginationMeta, PaymentMethod } from "@/types";
import { getRequestLanguage } from "@/utils/requestLanguage";

export interface TransactionsQueryParams {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  status?: "Completed" | "Pending" | "Returned" | "Voided";
  paymentMethod?: "CASH" | "QRIS" | "TRANSFER";
  customerId?: string;
  outletId?: string; // Filter by outlet for multi-outlet support
  storeId?: string; // Filter by store (for owner viewing all outlets)
  search?: string;
  sortBy?: "createdAt" | "total" | "status" | "paymentMethod";
  sortOrder?: "asc" | "desc";
}

export interface DailySummary {
  date: string;
  totalSales: number;
  totalTransactions: number;
  totalDiscount: number;
  totalTax: number;
  totalProfit: number;
  transactionsByStatus: {
    completed: number;
    pending: number;
    void: number;
  };
  salesByPaymentMethod: {
    cash: number;
    card: number;
    transfer: number;
    qris: number;
  };
}

export interface VoidTransactionData {
  reason: string;
  outletDrawerId?: string;
}

export interface ProcessReturnData {
  transactionId: string;
  items: Array<{
    transactionItemId: string;
    quantity: number;
  }>;
  reason: string;
  payment?: {
    method: PaymentMethod;
    outletDrawerId?: string;
  };
}

export interface SendReceiptWhatsAppData {
  phoneNumber: string;
  format?: "text" | "pdf" | "template";
}

export interface SendReceiptEmailData {
  email: string;
}

export interface ExportTransactionsParams {
  format: "csv" | "excel" | "pdf";
  fields: string[];
  transactionIds?: string[];
  status?: string[];
  paymentMethod?: string[];
  startDate?: string;
  endDate?: string;
  storeId?: string;
  outletId?: string;
}

export interface TransactionsPaginatedResponse {
  transactions: Transaction[];
  pagination: PaginationMeta;
}

export const transactionApi = api.injectEndpoints({
  endpoints: builder => ({
    // Get transactions with pagination and filters
    getTransactions: builder.query<
      ApiResponse<TransactionsPaginatedResponse>,
      TransactionsQueryParams
    >({
      query: params => ({
        url: "/transactions",
        params,
      }),
      providesTags: ["Transaction"],
    }),

    // Get single transaction
    getTransaction: builder.query<ApiResponse<Transaction>, string>({
      query: id => `/transactions/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Transaction", id }],
    }),

    // Get daily summary
    getDailySummary: builder.query<ApiResponse<DailySummary>, { date?: string; outletId?: string; storeId?: string }>({
      query: params => ({
        url: "/transactions/daily-summary",
        params,
      }),
      providesTags: ["Transaction"],
    }),

    // Get period summary (for date ranges)
    getPeriodSummary: builder.query<
      ApiResponse<DailySummary>,
      { startDate?: string; endDate?: string; outletId?: string; storeId?: string }
    >({
      query: params => ({
        url: "/transactions/period-summary",
        params,
      }),
      providesTags: ["Transaction"],
    }),

    // Void transaction
    voidTransaction: builder.mutation<
      ApiResponse<Transaction>,
      { id: string; data: VoidTransactionData }
    >({
      query: ({ id, data }) => ({
        url: `/transactions/${id}/void`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Transaction", id },
        "Transaction",
        "TransactionStats",
        "Report",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(inventoryApi.util.invalidateTags(["Item", "ItemStock", "InventoryStats"]));
        } catch {
          return;
        }
      },
    }),

    // Assign customer to existing transaction
    assignTransactionCustomer: builder.mutation<
      ApiResponse<Transaction>,
      { id: string; customerId: string }
    >({
      query: ({ id, customerId }) => ({
        url: `/transactions/${id}/assign-customer`,
        method: "POST",
        body: { customerId },
      }),
      invalidatesTags: (_result, _error, { id, customerId }) => {
        const tags: Array<{ type: "Transaction" | "Customer"; id?: string } | "Transaction" | "Customer"> = [
          { type: "Transaction" as const, id },
          "Transaction" as const,
        ];

        if (customerId) {
          tags.push({ type: "Customer" as const, id: customerId });
        }

        const previousCustomerId = _result?.meta?.previousCustomerId;
        if (previousCustomerId && previousCustomerId !== customerId) {
          tags.push({ type: "Customer" as const, id: previousCustomerId });
        }

        return tags;
      },
    }),

    // Process return
    processReturn: builder.mutation<ApiResponse<Transaction>, ProcessReturnData>({
      query: data => ({
        url: "/transactions/return",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "Transaction",
        "TransactionStats",
        "Item",
        "ItemStock",
        "CashDrawer",
        "StoreCash",
        "Report",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(inventoryApi.util.invalidateTags(["Item", "ItemStock", "InventoryStats"]));
        } catch {
          return;
        }
      },
    }),

    // Get receipt in different formats
    getReceiptJSON: builder.query<ApiResponse<any>, string>({
      query: id => `/transactions/${id}/receipt`,
    }),

    getReceiptHTML: builder.query<string, string>({
      query: id => ({
        url: `/transactions/${id}/receipt/html`,
        responseHandler: (response: Response) => response.text(),
      }),
    }),

    getReceiptText: builder.query<string, string>({
      query: id => ({
        url: `/transactions/${id}/receipt/text`,
        responseHandler: (response: Response) => response.text(),
      }),
    }),

    // Download receipt PDF
    downloadReceiptPDF: builder.query<Blob, string>({
      query: id => ({
        url: `/transactions/${id}/receipt/pdf`,
        params: { lang: getRequestLanguage() },
        responseHandler: async (response: Response) => response.blob(),
      }),
      keepUnusedDataFor: 0, // Don't cache blob data
    }),

    // Send receipt via WhatsApp
    sendReceiptWhatsApp: builder.mutation<
      ApiResponse<{ success: boolean; messageId?: string }>,
      { id: string; data: SendReceiptWhatsAppData }
    >({
      query: ({ id, data }) => ({
        url: `/transactions/${id}/receipt/whatsapp`,
        method: "POST",
        body: data,
      }),
    }),

    // Send receipt via Email
    sendReceiptEmail: builder.mutation<
      ApiResponse<{ success: boolean; messageId?: string }>,
      { id: string; data: SendReceiptEmailData }
    >({
      query: ({ id, data }) => ({
        url: `/transactions/${id}/receipt/email`,
        method: "POST",
        body: data,
      }),
    }),

    // Export transactions (mutations don't cache by default, so blob is safe)
    exportTransactions: builder.mutation<Blob, ExportTransactionsParams>({
      query: params => ({
        url: "/transactions/export",
        method: "POST",
        body: params,
        responseHandler: async (response: Response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useGetTransactionQuery,
  useGetDailySummaryQuery,
  useGetPeriodSummaryQuery,
  useVoidTransactionMutation,
  useAssignTransactionCustomerMutation,
  useProcessReturnMutation,
  useGetReceiptJSONQuery,
  useGetReceiptHTMLQuery,
  useGetReceiptTextQuery,
  useLazyDownloadReceiptPDFQuery,
  useSendReceiptWhatsAppMutation,
  useSendReceiptEmailMutation,
  useExportTransactionsMutation,
} = transactionApi;
